import { createSlice } from "@reduxjs/toolkit";
import { sessionService } from "redux-react-session";
import ReactGA from "react-ga4";
import {
	BASE_API_URL,
	SHIP_TYPES,
	PAYMENT_METHODS,
	CART_STEPS,
} from "../constants";
import { runUnauthorizedActions } from "features/account/loginSlice";
import { setAlert, toggleShowAlert } from "features/alerts/alertsSlice";
import {
	setOrderConfirmationId,
	fetchOrderConfirmation,
	toggleShowOrderConfirmation,
	receiveOrderConfirmation,
} from "features/orders/orderConfirmationSlice";
import axios from "axios";
import { fetchPaymentCardListIfNeeded } from "./paymentCardSlice";
import { fetchDealerShipAddressListIfNeeded } from "./dealerShipAddressSlice";
import { fetchAccountCards } from "features/account/accountCardsSlice";

const initialState = {
	isLoading: false,
	isProcessing: false,
	isOpen: false,
	showTab: 1,
	cartObject: null,
	shipType: 6,
	shipAddress: null,
	shipAddressItem: null,
	shipLocationId: null,
	invoiceAddress: 0,
	invoiceAddressItem: { Alias: "FAMILY" },
	paymentType: 2,
	paymentCard: 0,
	creditCardResponse: null,
	payPalResponse: null,
	safetyPayResponse: null,
	depositResponse: null,
	paynetResponse: null,
	openPayLoaded: false,
	isOpenPoints: false,
	pointsData: null,
};
const cart = createSlice({
	name: "cart",
	initialState,
	reducers: {
		requestCartObject: (state) => {
			state.isLoading = true;
		},
		receiveCartObject: (state, action) => {
			state.isLoading = false;
			state.cartObject = action.payload.cartObject;
		},
		removeCartLoading: (state) => {
			state.isLoading = false;
		},
		setCartActiveTab: (state, action) => {
			switch (action.payload.showTab) {
				case 1:
				default:
					ReactGA.event({
						category: "Carrito",
						action: "Detalles",
					});
					break;
				case 2:
					ReactGA.event({
						category: "Carrito",
						action: "Envío",
					});
					break;
				case 3:
					ReactGA.event({
						category: "Carrito",
						action: "Mis Datos",
					});
					break;
				case 4:
					ReactGA.event({
						category: "Carrito",
						action: "Pago",
					});
					break;
				case 5:
					ReactGA.event({
						category: "Carrito",
						action: "Resumen",
					});
					break;
			}
			state.showTab = action.payload.showTab;
		},
		setCartShipType: (state, action) => {
			const shipType = action.payload.shipType;
			if (shipType > 0) {
				ReactGA.event({
					category: "Compra",
					action: "Seleccionar Tipo de Envío",
					label: SHIP_TYPES.properties[shipType].name,
				});
			}
			state.shipType = shipType;
		},
		setCartShipAddress: (state, action) => {
			state.shipAddress = action.payload.shipAddress;
			state.shipAddressItem = action.payload.shipAddressItem;
		},
		clearCartShipAddress: (state) => {
			state.shipAddress = null;
			state.shipAddressItem = null;
		},
		setCartUserData: (state, action) => {
			state.email = action.payload.email;
			state.phone = action.payload.phone;
		},
		setCartShipLocationId: (state, action) => {
			state.shipLocationId = action.payload.shipLocationId;
		},
		clearCartShipLocationId: (state) => {
			state.shipLocationId = null;
		},
		setCartPaymentType: (state, action) => {
			const paymentType = action.payload.paymentType;
			ReactGA.event({
				category: "Compra",
				action: "Seleccionar Método de Pago",
				label: PAYMENT_METHODS.properties[paymentType].name,
			});
			state.paymentType = paymentType;
		},
		toggleShowCart: (state, action) => {
			state.isOpen = !action.payload.isOpen;
		},
		setPaymentProcess: (state, action) => {
			state.isProcessing = action.payload.isProcessing;
		},
		receivePaymentCard: (state, action) => {
			state.isProcessing = false;
			state.creditCardResponse = action.payload.creditCardResponse;
		},
		receivePaymentPayPal: (state, action) => {
			state.isProcessing = false;
			state.payPalResponse = action.payload.payPalResponse;
		},
		receivePaymentSafetyPay: (state, action) => {
			state.isProcessing = false;
			state.safetyPayResponse = action.payload.safetyPayResponse;
		},
		receivePaymentDeposit: (state, action) => {
			state.isProcessing = false;
			state.depositResponse = action.payload.depositResponse;
		},
		receivePaymentPaynet: (state, action) => {
			state.isProcessing = false;
			state.paynetResponse = action.payload.paynetResponse;
		},
		setCartOpenPayLoaded: (state, action) => {
			state.openPayLoaded = action.payload.openPayLoaded;
		},
		setCartPaymentCard: (state, action) => {
			state.paymentCard = action.payload.paymentCard;
			state.paymentCardItem = action.payload.paymentCardItem;
		},
		toggleShowPoints: (state, action) => {
			state.isOpenPoints = !action.payload.isOpenPoints;
			state.pointsData =
				action.payload.pointsData != null ? action.payload.pointsData : null;
		},
		clearCartData: () => initialState,
	},
});

export const {
	requestCartObject,
	receiveCartObject,
	removeCartLoading,
	setCartActiveTab,
	setCartShipType,
	setCartShipAddress,
	clearCartShipAddress,
	setCartUserData,
	setCartShipLocationId,
	clearCartShipLocationId,
	setCartPaymentType,
	toggleShowCart,
	setPaymentProcess,
	receivePaymentCard,
	receivePaymentPayPal,
	receivePaymentSafetyPay,
	receivePaymentDeposit,
	receivePaymentPaynet,
	setCartOpenPayLoaded,
	setCartPaymentCard,
	toggleShowPoints,
	clearCartData,
} = cart.actions;

const getCartAlias = (cart) => {
	let alias = "ContiShop";
	switch (cart.shipType) {
		case SHIP_TYPES.ADDRESS:
			alias = cart.shipAddressItem.Alias;
			break;
		case SHIP_TYPES.FACTORY:
			alias = "Factory";
			break;
		case SHIP_TYPES.DEALER:
			alias = cart.shipAddressItem.Name;
			break;
		default:
			alias = "ContiShop";
			break;
	}
	return alias;
};

const shouldFetchCartObject = (state) => {
	const { cart } = state;
	if (cart.cartObject == null) {
		return true;
	}
	if (cart.cartObject.strIdBolsa === "") {
		return true;
	}
	return false;
};

export const fetchCartObjectIfNeeded = () => async (dispatch, getState) => {
	const theState = getState();
	if (shouldFetchCartObject(theState)) {
		return dispatch(fetchCartObject());
	}
};

export const fetchCartObject = () => async (dispatch, getState) => {
	sessionService
		.loadSession()
		.then((session) => {
			const headers = {
				Authorization: "bearer " + session.loginData.access_token,
				"Content-Type": "application/json",
			};
			const options = {
				headers: headers,
			};
			const theState = getState();
			let qs = [];
			if (
				theState.cart.cartObject != null &&
				theState.cart.cartObject.strIdBolsa !== ""
			) {
				qs.push("id=" + theState.cart.cartObject.strIdBolsa);
			}
			dispatch(requestCartObject());
			let url = BASE_API_URL + "/api/FamilyFriends/Carritos/MiCarrito";
			if (qs.length > 0) {
				url += "/?" + qs.join("&");
			}
			return axios
				.get(url, options)
				.then((response) => {
					dispatch(receiveCartObject({ cartObject: response.data }));
				})
				.catch((error) => {
					// If 401 Unauthorized login failed
					if (error.response.status === 401) {
						dispatch(runUnauthorizedActions());
					}
				});
		})
		.catch((err) => console.log("Error", err));
};

export const fetchAddItemToCartObject =
	(tireId, qty) => async (dispatch, getState) => {
		sessionService
			.loadSession()
			.then((session) => {
				const headers = {
					Authorization: "bearer " + session.loginData.access_token,
					"Content-Type": "application/json",
				};
				const theState = getState();
				const cartId =
					theState.cart.cartObject != null &&
					theState.cart.cartObject.strIdBolsa !== ""
						? theState.cart.cartObject.strIdBolsa
						: null;
				const options = {
					headers: headers,
				};
				const body = {
					Id: cartId,
					TireId: tireId,
					Qty: qty,
				};
				dispatch(requestCartObject());
				const url = BASE_API_URL + "/api/FamilyFriends/Carritos/Agregar";
				return axios
					.post(url, JSON.stringify(body), options)
					.then((response) => {
						dispatch(receiveCartObject({ cartObject: response.data }));
					})
					.catch((error) => {
						// If 401 Unauthorized login failed
						if (error.response.status === 401) {
							dispatch(runUnauthorizedActions());
						}
						// 400 Bad Request
						if (error.response.status === 401) {
							dispatch(removeCartLoading());
							dispatch(
								setAlert({
									alert: {
										alert: {
											title: "¡Lo sentimos!",
											description: "No se pudo agregar el producto al carrito.",
											details: error.Message,
										},
									},
								})
							);
							dispatch(toggleShowAlert({ isOpen: false }));
						}
					});
			})
			.catch((err) => console.log("Error", err));
	};

export const fetchDeleteItemFromCartObject =
	(tireId) => async (dispatch, getState) => {
		sessionService
			.loadSession()
			.then((session) => {
				const headers = {
					Authorization: "bearer " + session.loginData.access_token,
					"Content-Type": "application/json",
				};
				const theState = getState();
				const cartId =
					theState.cart.cartObject != null &&
					theState.cart.cartObject.strIdBolsa !== ""
						? theState.cart.cartObject.strIdBolsa
						: null;
				const options = {
					headers: headers,
				};
				const body = {
					Id: cartId,
					TireId: tireId,
				};
				dispatch(requestCartObject());
				const url = BASE_API_URL + "/api/FamilyFriends/Carritos/Eliminar";
				return axios
					.post(url, JSON.stringify(body), options)
					.then((response) => {
						dispatch(receiveCartObject({ cartObject: response.data }));
					})
					.catch((error) => {
						// If 401 Unauthorized login failed
						if (error.response.status === 401) {
							dispatch(runUnauthorizedActions());
						}
					});
			})
			.catch((err) => console.log("Error", err));
	};

export const fetchPaymentCard = (data) => async (dispatch, getState) => {
	sessionService
		.loadSession()
		.then((session) => {
			const headers = {
				Authorization: "bearer " + session.loginData.access_token,
				"Content-Type": "application/json",
			};
			const theState = getState();
			const alias = getCartAlias(theState.cart);
			const coupon =
				theState.cart.coupon == null ? null : theState.cart.coupon.Code;
			const token =
				theState.cart.paymentCard === 0
					? {
							tokenId: theState.accountCardAdd.token.id,
							deviceSessionId: theState.accountCardAdd.sessId,
							card: theState.accountCardAdd.token.card,
							save: data.save,
							usePoints: data.usePoints,
					  }
					: null;
			const card =
				theState.cart.paymentCard > 0
					? {
							cardId: theState.cart.paymentCard,
							cvv: data.code,
							deviceSessionId: theState.accountCardAdd.sessId,
							usePoints: data.usePoints,
					  }
					: null;
			const body = {
				id: theState.cart.cartObject.strIdBolsa,
				delivery: theState.cart.shipType,
				alias: alias,
				invoice: theState.cart.invoiceAddressItem.Alias,
				payment: theState.cart.paymentType,
				promo: data.cardPromo,
				coupon: coupon,
				authorized: "",
				LocationId:
					theState.cart.shipLocationId != null
						? theState.cart.shipLocationId
						: 0,
				card: card,
				token: token,
			};
			const options = {
				headers: headers,
			};
			ReactGA.event({
				category: "Compra",
				action: "Finalizar compra",
				label: "Compra con Tarjeta de Crédito",
			});
			dispatch(setPaymentProcess({ isProcessing: true }));
			const url = BASE_API_URL + "/api/Carritos/Finalizar/OpenPayTarjeta";
			return axios
				.post(url, JSON.stringify(body), options)
				.then((response) => {
					if (response.data.Url != null) {
						window.location.replace(response.data.Url);
					}
					if (response.data.Id != null) {
						dispatch(setOrderConfirmationId({ orderId: response.data.Id }));
						dispatch(fetchOrderConfirmation());
						dispatch(setPaymentProcess({ isProcessing: false }));
						dispatch(toggleShowCart({ isOpen: true }));
						dispatch(toggleShowOrderConfirmation({ isOpen: false }));
						dispatch(fetchCartObject());
						dispatch(fetchAccountCards());
					}
				})
				.catch((error) => {
					// If 401 Unauthorized login failed
					if (error.response.status === 401) {
						dispatch(runUnauthorizedActions());
					} else {
						dispatch(setPaymentProcess({ isProcessing: false }));
						// 404 Not Found
						if (error.response.status === 404) {
							dispatch(fetchAccountCards());
							dispatch(setCartPaymentCard({ paymentCard: 0 }));
							dispatch(setCartActiveTab({ showTab: CART_STEPS.PAYMENT }));
							dispatch(
								setAlert({
									alert: {
										title: "¡Lo sentimos!",
										description:
											"No se ha podido crear el pedido por la siguiente causa:",
										details: "No se encontró la tarjeta guardada.",
									},
								})
							);
							dispatch(toggleShowAlert({ isOpen: false }));
						}
						// 400 BadRequest
						if (error.response.status === 400) {
							if (error.response.data.Message != null) {
								dispatch(
									setAlert({
										alert: {
											title: "¡Lo sentimos!",
											description:
												"No se ha podido crear el pedido por la siguiente causa:",
											details: error.response.data.Message,
										},
									})
								);
								dispatch(toggleShowAlert({ isOpen: false }));
							}
						}
					}
				});
		})
		.catch((err) => console.log("Error", err));
};

export const fetchPaymentPayPal = () => async (dispatch, getState) => {
	sessionService
		.loadSession()
		.then((session) => {
			const headers = {
				Authorization: "bearer " + session.loginData.access_token,
				"Content-Type": "application/json",
			};
			const theState = getState();
			const alias = getCartAlias(theState.cart);
			const options = {
				method: "POST",
				headers: headers,
				body: JSON.stringify({
					id: theState.cart.cartObject.strIdBolsa,
					delivery: theState.cart.shipType,
					alias: alias,
					invoice: theState.cart.invoiceAddressItem.Alias,
					payment: theState.cart.paymentType,
					promo: 0,
					LocationId:
						theState.cart.shipLocationId != null
							? theState.cart.shipLocationId
							: 0,
					coupon: null,
					authorized: "",
				}),
			};
			ReactGA.event({
				category: "Compra",
				action: "Finalizar compra",
				label: "Compra con PayPal",
			});
			dispatch(setPaymentProcess({ isProcessing: true }));
			const url = BASE_API_URL + "/api/Carritos/Finalizar/PayPal";
			let responseStatus = 0;
			return fetch(url, options)
				.then((response) => {
					responseStatus = response.status;
					if (response.ok) {
						return response.json();
					} else {
						return response.json().then((error) => Promise.reject(error));
					}
				})
				.then((json) => {
					dispatch(receivePaymentPayPal({ payPalResponse: json }));
					dispatch(fetchCartObject());
				})
				.catch((error) => {
					dispatch(setPaymentProcess({ isProcessing: false }));
					// TODO: fetch error actions
					// If 401 Unauthorized login failed
					if (responseStatus === 401) {
						dispatch(runUnauthorizedActions());
					} else {
						dispatch(
							setAlert({
								alert: {
									title: "¡Lo sentimos!",
									description:
										"No se ha podido crear el pedido por la siguiente causa:",
									details: error.Message,
								},
							})
						);
						dispatch(toggleShowAlert({ isOpen: false }));
					}
				});
		})
		.catch((err) => console.log("Error", err));
};

export const fetchPaymentSafetyPay = () => async (dispatch, getState) => {
	sessionService
		.loadSession()
		.then((session) => {
			const headers = {
				Authorization: "bearer " + session.loginData.access_token,
				"Content-Type": "application/json",
			};
			const theState = getState();
			const alias = getCartAlias(theState.cart);
			const options = {
				method: "POST",
				headers: headers,
				body: JSON.stringify({
					id: theState.cart.cartObject.strIdBolsa,
					delivery: theState.cart.shipType,
					alias: alias,
					invoice: theState.cart.invoiceAddressItem.Alias,
					payment: theState.cart.paymentType,
					promo: 0,
					LocationId:
						theState.cart.shipLocationId != null
							? theState.cart.shipLocationId
							: 0,
					coupon: null,
					authorized: "",
				}),
			};
			ReactGA.event({
				category: "Compra",
				action: "Finalizar compra",
				label: "Compra con SafetyPay",
			});
			dispatch(setPaymentProcess({ isProcessing: true }));
			const url = BASE_API_URL + "/api/Carritos/Finalizar/SafetyPay";
			let responseStatus = 0;
			return fetch(url, options)
				.then((response) => {
					responseStatus = response.status;
					if (response.ok) {
						return response.json();
					} else {
						return response.json().then((error) => Promise.reject(error));
					}
				})
				.then((json) => {
					dispatch(receivePaymentSafetyPay({ safetyPayResponse: json }));
					dispatch(fetchCartObject());
				})
				.catch((error) => {
					dispatch(setPaymentProcess({ isProcessing: false }));
					// TODO: fetch error actions
					// If 401 Unauthorized login failed
					if (responseStatus === 401) {
						dispatch(runUnauthorizedActions());
					} else {
						dispatch(
							setAlert({
								alert: {
									title: "¡Lo sentimos!",
									description:
										"No se ha podido crear el pedido por la siguiente causa:",
									details: error.Message,
								},
							})
						);
						dispatch(toggleShowAlert({ isOpen: false }));
					}
				});
		})
		.catch((err) => console.log("Error", err));
};

export const fetchPaymentDeposit = () => async (dispatch, getState) => {
	sessionService
		.loadSession()
		.then((session) => {
			const headers = {
				Authorization: "bearer " + session.loginData.access_token,
				"Content-Type": "application/json",
			};
			const theState = getState();
			const alias = getCartAlias(theState.cart);
			const options = {
				method: "POST",
				headers: headers,
				body: JSON.stringify({
					id: theState.cart.cartObject.strIdBolsa,
					delivery: theState.cart.shipType,
					alias: alias,
					invoice: theState.cart.invoiceAddressItem.Alias,
					payment: theState.cart.paymentType,
					promo: 0,
					LocationId:
						theState.cart.shipLocationId != null
							? theState.cart.shipLocationId
							: 0,
					coupon: null,
					authorized: "",
				}),
			};
			ReactGA.event({
				category: "Compra",
				action: "Finalizar compra",
				label: "Compra con SafetyPay",
			});
			dispatch(setPaymentProcess({ isProcessing: true }));
			const url = BASE_API_URL + "/api/Carritos/Finalizar/Deposito";
			let responseStatus = 0;
			return fetch(url, options)
				.then((response) => {
					responseStatus = response.status;
					if (response.ok) {
						return response.json();
					} else {
						return response.json().then((error) => Promise.reject(error));
					}
				})
				.then((json) => {
					dispatch(receivePaymentDeposit({ depositResponse: json }));
					dispatch(setOrderConfirmationId({ orderId: json.Order }));
					dispatch(fetchOrderConfirmation());
					dispatch(toggleShowCart({ isOpen: theState.cart.isOpen }));
					dispatch(clearCartData());
					dispatch(fetchCartObject());
					dispatch(toggleShowOrderConfirmation({ isOpen: false }));
				})
				.catch((error) => {
					dispatch(setPaymentProcess({ isProcessing: false }));
					// TODO: fetch error actions
					// If 401 Unauthorized login failed
					if (responseStatus === 401) {
						dispatch(runUnauthorizedActions());
					} else {
						dispatch(
							setAlert({
								alert: {
									title: "¡Lo sentimos!",
									description:
										"No se ha podido crear el pedido por la siguiente causa:",
									details: error.Message,
								},
							})
						);
						dispatch(toggleShowAlert({ isOpen: false }));
					}
				});
		})
		.catch((err) => console.log("Error", err));
};

export const fetchPaymentPaynet = () => async (dispatch, getState) => {
	sessionService
		.loadSession()
		.then((session) => {
			const headers = {
				Authorization: "bearer " + session.loginData.access_token,
				"Content-Type": "application/json",
			};
			const theState = getState();
			const alias = getCartAlias(theState.cart);
			const options = {
				method: "POST",
				headers: headers,
				body: JSON.stringify({
					id: theState.cart.cartObject.strIdBolsa,
					delivery: theState.cart.shipType,
					alias: alias,
					invoice: theState.cart.invoiceAddressItem.Alias,
					payment: theState.cart.paymentType,
					promo: 0,
					LocationId:
						theState.cart.shipLocationId != null
							? theState.cart.shipLocationId
							: 0,
					coupon: null,
					authorized: "",
				}),
			};
			ReactGA.event({
				category: "Compra",
				action: "Finalizar compra",
				label: "Compra con Paynet",
			});
			dispatch(setPaymentProcess({ isProcessing: true }));
			const url = BASE_API_URL + "/api/Carritos/Finalizar/Paynet";
			let responseStatus = 0;
			return fetch(url, options)
				.then((response) => {
					responseStatus = response.status;
					if (response.ok) {
						return response.json();
					} else {
						return response.json().then((error) => Promise.reject(error));
					}
				})
				.then((json) => {
					dispatch(receivePaymentPaynet({ paynetResponse: json }));
					dispatch(setOrderConfirmationId({ orderId: json.Order }));
					dispatch(fetchOrderConfirmation());
					dispatch(toggleShowCart({ isOpen: theState.cart.isOpen }));
					dispatch(clearCartData());
					dispatch(fetchCartObject());
					dispatch(toggleShowOrderConfirmation({ isOpen: false }));
				})
				.catch((error) => {
					dispatch(setPaymentProcess({ isProcessing: false }));
					// TODO: fetch error actions
					// If 401 Unauthorized login failed
					if (responseStatus === 401) {
						dispatch(runUnauthorizedActions());
					} else {
						dispatch(
							setAlert({
								alert: {
									title: "¡Lo sentimos!",
									description:
										"No se ha podido crear el pedido por la siguiente causa:",
									details: error.Message,
								},
							})
						);
						dispatch(toggleShowAlert({ isOpen: false }));
					}
				});
		})
		.catch((err) => console.log("Error", err));
};
export const evaluateCartNextStep = (nextTab) => async (dispatch, getState) => {
	const theState = getState();
	const { cartObject, shipType, paymentType } = theState.cart;
	sessionService
		.loadSession()
		.then(() => {
			let cartItems = [];
			for (let i = 0; i < cartObject.Elementos.length; i++) {
				cartItems.push({
					item_id: cartObject.Elementos[i].Tire.Code,
					item_name: cartObject.Elementos[i].Tire.TireProduct.Name,
					item_brand: cartObject.Elementos[i].Tire.TireProduct.Brand,
					item_category: "Llantas/" + cartObject.Elementos[i].Tire.VehicleType,
					item_variant: cartObject.Elementos[i].Tire.Size,
					price: cartObject.Elementos[i].Tire.Price,
					quantity: cartObject.Elementos[i].Cantidad,
				});
			}
			dispatch(setCartActiveTab({ showTab: nextTab }));
			dispatch(fetchPaymentCardListIfNeeded());
			dispatch(fetchDealerShipAddressListIfNeeded());
			switch (nextTab) {
				case CART_STEPS.SHIP:
					ReactGA.gtag("event", "begin_checkout", {
						currency: "MXN",
						value: cartObject.Total,
						coupon: cartObject.CuponDescuento.CodigoCupon,
						items: cartItems,
					});
					ReactGA.event({
						category: "Checkout",
						action: "Paso 1 - Carrito",
					});
					break;
				case CART_STEPS.PAYMENT:
					ReactGA.gtag("event", "add_shipping_info", {
						currency: "MXN",
						value: cartObject.Total,
						coupon: cartObject.CuponDescuento.CodigoCupon,
						shipping_tier: SHIP_TYPES.properties[shipType].name,
						items: cartItems,
					});
					ReactGA.event({
						category: "Checkout",
						action: "Paso 2 - Envío",
					});
					break;
				case CART_STEPS.SUMMARY:
					ReactGA.gtag("event", "add_payment_info", {
						currency: "MXN",
						value: cartObject.Total,
						coupon: cartObject.CuponDescuento.CodigoCupon,
						items: cartItems,
						payment_type: PAYMENT_METHODS.properties[paymentType].name,
					});
					ReactGA.event({
						category: "Checkout",
						action: "Paso 4 - Método de Pago",
					});
					break;
				default:
					break;
			}
		})
		.catch((err) => {
			console.log("Error", err);
		});
};
export const cartLoadOpenPay = () => async (dispatch) => {
	dispatch(requestCartObject());
	const openpay = window.OpenPay;
	openpay.setId(process.env.REACT_APP_OPENPAY_ID);
	openpay.setApiKey(process.env.REACT_APP_OPENPAY_PUBLIC);
	openpay.setSandboxMode(
		process.env.REACT_APP_OPENPAY_SANDBOX === "true" ? true : false
	);
	dispatch(setCartOpenPayLoaded({ openPayLoaded: true }));
	dispatch(removeCartLoading());
};

export default cart.reducer;
